import React, { useCallback, useEffect, useRef, useState } from "react";
import "./coach.css";
import { CONFIG as CFG } from "./config";
import { fetchKlines, BinanceStream } from "./binance";
import { fetchSpxKlines, SpxPoller, SPX_INTERVALS } from "./spx";
import { createChartInstance, setData, updateCandle, destroyChartInstance } from "./chartCore";
import { createVolumeProfile } from "./volumeProfile";
import { createOrderFlow } from "./orderFlow";
import { computeVWAP } from "./vwap";
import { createAtrGrid } from "./atrGrid";
import { createDrawTools } from "./drawTools";
import { createSessions } from "./sessions";
import LiveSessionPanel from "../liveSession/LiveSessionPanel";
import { loadDrawings, saveDrawings } from "./drawingsStorage";

const RAIL_TOOLS = [
  { title: "Cursor", tool: "cursor", icon: "⤶" },
  { title: "Trendlijn", tool: "trend", icon: "╱" },
  { title: "Horizontale lijn", tool: "hline", icon: "─" },
  { title: "Ray", tool: "ray", icon: "➛" },
  { title: "Pen", tool: "brush", icon: "✎" },
  { title: "Rechthoek", tool: "rect", icon: "▩" },
  { title: "Fibonacci", tool: "fib", icon: "F" },
  { title: "Fibonacci Transient (0.5)", tool: "fibtr", icon: "½" },
  { title: "Meetlat (prijs / tijd / volume)", tool: "measure", icon: "📏" },
  { title: "Tekst", tool: "text", icon: "T" },
];

const C = CFG.colors;

/**
 * Coach Terminal — React-poort van terminal_wyckoffpcg.html.
 *
 * Fase 1 (dit bestand): chart + databasis (Binance REST/WS, candles,
 * volume, status, OHLC-readout, symbol/timeframe-wissel).
 * Dock-panelen (Volume Profile, Order Flow, Footprint), overlays
 * (VWAP/Sessions/Zones/Wyckoff PCG/ATR Grid), tekengereedschap en de
 * live-sessie/chat volgen in latere fases — vandaar de "binnenkort"
 * placeholders in de dock en de inactieve teken-rail hieronder.
 */
export default function CoachTerminal({ onLogout, accountMenu }) {
  const [symbol, setSymbol] = useState(CFG.defaultSymbol);
  const [interval, setIntervalValue] = useState(CFG.defaultInterval);
  const [status, setStatus] = useState("loading"); // loading | live | offline
  const [ohlc, setOhlc] = useState(null);
  const [vpStats, setVpStats] = useState(null); // { poc, vah, val }
  const [vpOn, setVpOn] = useState(true);       // default aan
  const [vwapOn, setVwapOn] = useState(true);   // default aan, zoals het origineel
  const [atrOn, setAtrOn] = useState(false);    // default uit, zoals het origineel
  const [activeTool, setActiveTool] = useState("cursor");
  const [magnetOn, setMagnetOn] = useState(false);
  const [linesVisible, setLinesVisible] = useState(true);
  const [sessionsOn, setSessionsOn] = useState(true); // default aan, zoals het origineel

  const chartContainerRef = useRef(null);
  const chartApiRef = useRef(null);
  const streamRef = useRef(null);
  const candlesRef = useRef([]);
  const symbolRef = useRef(symbol);
  const vpRef = useRef(null);
  const orderFlowRef = useRef(null);
  const vwapSeriesRef = useRef(null);
  const vwapOnRef = useRef(vwapOn);
  const atrGridRef = useRef(null);
  const drawToolsRef = useRef(null);
  const sessionsRef = useRef(null);

  // Adapter die de tekengereedschap-staat aan het bestaande Live
  // Session-protocol (coach/student, via de Cloudflare Worker) koppelt.
  // useLiveSession/LiveSessionPanel verwachten een "cycleStore"-vormig
  // object ({ broadcastRef, applyRemoteCycles }); hier geven we ze
  // gewoon de volledige tekeningen-array van drawTools in plaats van
  // de oude cycle-annotaties. Geen wijziging nodig aan de Worker —
  // die stuurt het al als ondoorzichtige payload rond.
  const liveDrawStoreRef = useRef(null);
  if (!liveDrawStoreRef.current) {
    liveDrawStoreRef.current = {
      broadcastRef: { current: null },
      // Geeft useLiveSession de huidige lokale tekeningen zodat die ze bij
      // het verbinden meteen naar de room kan sturen (zie useLiveSession.js).
      getCurrentCycles: () => (drawToolsRef.current ? drawToolsRef.current.getAllLines() : []),
      applyRemoteCycles: (remoteLines) => {
        if (drawToolsRef.current) {
          drawToolsRef.current.applyRemote({ type: "replaceAll", lines: remoteLines || [] });
        }
      },
    };
  }

  useEffect(() => { symbolRef.current = symbol; }, [symbol]);
  useEffect(() => { vwapOnRef.current = vwapOn; }, [vwapOn]);

  // Bij wisselen naar SPX: als de huidige timeframe niet in SPX_INTERVALS
  // zit (bv. 4h, dat Binance wel maar Yahoo niet heeft), val terug op 15m
  // i.p.v. stil een lege/offline chart te tonen (zie de early-return in
  // het data-laad-effect hieronder).
  useEffect(() => {
    const cfg = CFG.symbols.find((s) => s.sym === symbol);
    if (cfg && cfg.source === "yahoo" && !SPX_INTERVALS.has(interval)) {
      setIntervalValue("15m");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol]);

  // Bij het wisselen van symbool: de tekeningen van het nieuwe symbool uit
  // localStorage laden (anker-tijden/prijzen zijn symbool-specifiek, dus
  // niet zinvol om over te nemen). Timeframe-wissels binnen hetzelfde
  // symbool raken dit niet — tekeningen blijven dan gewoon staan.
  const isFirstSymbolRender = useRef(true);
  useEffect(() => {
    if (isFirstSymbolRender.current) { isFirstSymbolRender.current = false; return; }
    if (!drawToolsRef.current) return;
    drawToolsRef.current.applyRemote({ type: "replaceAll", lines: loadDrawings(symbol) });
  }, [symbol]);

  // Dock-refs voor de order-flow module (direct-DOM, buiten React's
  // render-cyclus — zelfde performance-aanpak als het origineel)
  const ofListRef = useRef(null);
  const cvdValueRef = useRef(null);
  const ofStatsRef = useRef(null);

  // Chart + Volume Profile + Order Flow eenmalig aanmaken
  useEffect(() => {
    if (!chartContainerRef.current) return;
    const api = createChartInstance(chartContainerRef.current);
    chartApiRef.current = api;

    api.chart.subscribeCrosshairMove((param) => {
      if (!param || !param.time || !param.seriesData) { setOhlc(null); return; }
      const d = param.seriesData.get(api.candleSeries);
      if (!d) { setOhlc(null); return; }
      setOhlc({ ...d, up: d.close >= d.open });
    });

    const vp = createVolumeProfile(
      chartContainerRef.current,
      () => chartApiRef.current,
      () => candlesRef.current,
    );
    vp.onStats(setVpStats);
    vpRef.current = vp;

    const sessions = createSessions(
      chartContainerRef.current,
      () => chartApiRef.current,
      () => candlesRef.current,
    );
    sessionsRef.current = sessions;

    api.chart.timeScale().subscribeVisibleLogicalRangeChange(() => {
      vp.redraw();
      sessions.redraw();
    });

    const orderFlow = createOrderFlow(ofListRef.current, cvdValueRef.current, ofStatsRef.current);
    orderFlowRef.current = orderFlow;

    // VWAP-lijn (dag-geankerd), default aan
    const vwapSeries = api.chart.addLineSeries({
      color: "#9b8cff", lineWidth: 1, priceLineVisible: false,
      lastValueVisible: false, crosshairMarkerVisible: false,
    });
    vwapSeriesRef.current = vwapSeries;

    // ATR Grid (kwart-ATR-niveaus), default uit
    const atrGrid = createAtrGrid(
      () => chartApiRef.current,
      () => ({ candles: candlesRef.current, symbol: symbolRef.current }),
    );
    atrGridRef.current = atrGrid;

    // Tekengereedschap (trendlijn/horizontale lijn/ray/pen/rechthoek/
    // fibonacci/tekst) — tekent via een lightweight-charts primitive
    // boven de candle-serie, zie drawTools.js
    const drawTools = createDrawTools(
      chartContainerRef.current,
      () => chartApiRef.current,
      () => candlesRef.current,
    );
    drawTools.onToolChange((toolName, magnet) => {
      setActiveTool(toolName);
      setMagnetOn(magnet);
    });
    // Elke lokale tekenwijziging (toevoegen/slepen/verwijderen/wissen):
    // meesturen naar de Live Session-room (als actief) én bewaren in
    // localStorage, zodat tekeningen een page-reload/opnieuw inloggen
    // overleven (per symbool, puur browser-lokaal).
    drawTools.onLocalEdit(() => {
      const lines = drawTools.getAllLines();
      const broadcast = liveDrawStoreRef.current.broadcastRef.current;
      if (broadcast) broadcast(lines);
      saveDrawings(symbolRef.current, lines);
    });
    drawToolsRef.current = drawTools;

    // Bewaarde tekeningen voor het huidige symbool meteen laden.
    drawTools.applyRemote({ type: "replaceAll", lines: loadDrawings(symbolRef.current) });

    return () => {
      drawTools.destroy();
      atrGrid.clear();
      sessions.destroy();
      vp.destroy();
      destroyChartInstance(api);
    };
  }, []);

  const redrawVwap = useCallback(() => {
    if (!vwapOnRef.current || !vwapSeriesRef.current) return;
    vwapSeriesRef.current.setData(computeVWAP(candlesRef.current));
  }, []);

  // Data laden + live stream bij symbool/timeframe-wissel
  useEffect(() => {
    let cancelled = false;
    // BUG-FIX: de live stream verbindt vrijwel instant, maar fetchKlines()
    // (historische REST-data voor het NIEUWE symbool/timeframe) kan zomaar
    // een paar honderd ms tot enkele seconden duren (3000 candles → tot 3
    // gepagineerde requests). Zonder deze vlag kon een live onKline-update
    // al binnenkomen VOORDAT fetchKlines klaar was — en werd 'ie dan
    // toegepast op/geduwd in candlesRef.current terwijl dat array nog de
    // data van het VORIGE timeframe bevatte. Resultaat: een candle van bv.
    // het nieuwe 15m-interval tussen de oude 5m-candles, met een rare
    // tijdsprong/spacing als gevolg — en omdat drawTools.js's
    // tijd↔pixel-conversie (timeToLogical/logicalToTimeInterp) óók op dit
    // array leunt, raakten bestaande tekeningen in diezelfde fractie van
    // een seconde mee verkeerd gepositioneerd. Door live updates te
    // negeren totdat de historie er echt staat, kan dit niet meer gebeuren.
    let historyLoaded = false;
    setStatus("loading");

    if (orderFlowRef.current) orderFlowRef.current.reset();
    setVpStats(null);

    // SPX (Yahoo-bron) heeft geen WebSocket-feed en een beperktere set
    // timeframes dan Binance — zie spx.js. Valt 'interval' buiten die set
    // (bv. net overgeschakeld van Binance op SPX op een tf die SPX niet
    // heeft), dan negeren we deze run; de tf-bar hieronder filtert zulke
    // knoppen al weg zodra SPX actief is, dus dit is puur een vangnet.
    const cfg = CFG.symbols.find((s) => s.sym === symbol);
    const useYahoo = cfg && cfg.source === "yahoo";
    if (useYahoo && !SPX_INTERVALS.has(interval)) {
      setStatus("offline");
      return () => { cancelled = true; };
    }
    const fetchHistory = useYahoo ? () => fetchSpxKlines(interval) : () => fetchKlines(symbol, interval);

    fetchHistory().then((candles) => {
      if (cancelled || !chartApiRef.current) return;
      candlesRef.current = candles;
      historyLoaded = true;
      setData(chartApiRef.current, candles);
      chartApiRef.current.chart.timeScale().fitContent();
      if (vpRef.current) vpRef.current.redraw();
      if (sessionsRef.current) sessionsRef.current.redraw();
      redrawVwap();
      if (atrGridRef.current) atrGridRef.current.recompute();
    }).catch((err) => {
      console.error("[coach] historische data ophalen faalde:", err);
      if (!cancelled) setStatus("offline");
    });

    const stream = useYahoo ? new SpxPoller() : new BinanceStream();
    streamRef.current = stream;
    stream.connect(symbol, interval, {
      onStatus: (s) => { if (!cancelled) setStatus(s); },
      onKline: (c) => {
        if (cancelled || !chartApiRef.current || !historyLoaded) return;
        updateCandle(chartApiRef.current, c);
        const arr = candlesRef.current;
        if (arr.length && arr[arr.length - 1].time === c.time) arr[arr.length - 1] = c;
        else arr.push(c);
        if (vpRef.current) vpRef.current.redraw();
        if (sessionsRef.current) sessionsRef.current.redraw();
        redrawVwap();
        if (c.closed && atrGridRef.current) atrGridRef.current.recompute();
      },
      onTrade: (t) => {
        if (cancelled || !orderFlowRef.current) return;
        orderFlowRef.current.pushTrade(t);
      },
    });

    if (atrGridRef.current) atrGridRef.current.recompute();

    return () => {
      cancelled = true;
      stream.close();
    };
  }, [symbol, interval, redrawVwap]);

  const toggleVp = useCallback(() => {
    setVpOn((prev) => {
      const next = !prev;
      if (vpRef.current) vpRef.current.setVisible(next);
      return next;
    });
  }, []);

  const toggleVwap = useCallback(() => {
    setVwapOn((prev) => {
      const next = !prev;
      vwapOnRef.current = next;
      if (vwapSeriesRef.current) vwapSeriesRef.current.applyOptions({ visible: next });
      if (next) redrawVwap();
      return next;
    });
  }, [redrawVwap]);

  const toggleAtr = useCallback(() => {
    setAtrOn((prev) => {
      const next = !prev;
      if (atrGridRef.current) atrGridRef.current.setVisible(next);
      return next;
    });
  }, []);

  const toggleSessions = useCallback(() => {
    setSessionsOn((prev) => {
      const next = !prev;
      if (sessionsRef.current) sessionsRef.current.setVisible(next);
      return next;
    });
  }, []);

  const handleTfClick = useCallback((v) => setIntervalValue(v), []);

  // Bron van het huidige symbool (zie config.js) — "binance" (default) of
  // "yahoo" (alleen SPX, zie spx.js). Bepaalt welke fetch/stream-functies
  // hieronder gebruikt worden én welke timeframes getoond worden (Yahoo's
  // gratis intraday-granulariteit is beperkter dan Binance's).
  const symbolCfg = CFG.symbols.find((s) => s.sym === symbol);
  const isSpx = symbolCfg && symbolCfg.source === "yahoo";

  return (
    <div className="coach-terminal">
      <header className="top-chrome">
        <div className="tc-left">
          <select
            className="symbol-select"
            value={symbol}
            onChange={(e) => setSymbol(e.target.value)}
          >
            {CFG.symbols.map((s) => (
              <option key={s.sym} value={s.sym}>{s.label}</option>
            ))}
          </select>
          <span className="symbol-suffix">.P</span>
          <span className={`status-pill ${status}`}>
            {status === "live" ? "LIVE" : status === "loading" ? "LADEN…" : "OFFLINE"}
          </span>
        </div>

        <div className="tc-center">
          <div className="tf-bar">
            {CFG.intervals
              .filter((tf) => !isSpx || SPX_INTERVALS.has(tf.v))
              .map((tf) => (
                <button
                  key={tf.v}
                  className={`tf-btn ${tf.v === interval ? "active" : ""}`}
                  onClick={() => handleTfClick(tf.v)}
                >
                  {tf.label}
                </button>
              ))}
          </div>
        </div>

        <div className="tc-right">
          {accountMenu}
          {onLogout && (
            <button className="icon-btn" title="Uitloggen" onClick={onLogout}>&#9211;</button>
          )}
        </div>
      </header>

      <main className="main">
        <nav className="draw-rail" aria-label="Tekengereedschap">
          {RAIL_TOOLS.map(({ title, tool, icon }) => (
            <button
              key={tool}
              className={`rail-btn ${activeTool === tool ? "active" : ""}`}
              title={title}
              onClick={() => drawToolsRef.current && drawToolsRef.current.setTool(tool)}
            >
              {icon}
            </button>
          ))}
          <button
            className={`rail-btn ${magnetOn ? "active" : ""}`}
            title="Magneet (snap naar OHLC)"
            onClick={() => drawToolsRef.current && setMagnetOn(drawToolsRef.current.toggleMagnet())}
          >
            &#129522;
          </button>
          <span className="rail-spacer" />
          <button
            className={`rail-btn ${!linesVisible ? "active" : ""}`}
            title={linesVisible ? "Verberg alle tekeningen" : "Toon alle tekeningen"}
            onClick={() =>
              drawToolsRef.current && setLinesVisible(drawToolsRef.current.toggleVisibility())
            }
          >
            {"\u{1F441}️"}
          </button>
          <button
            className="rail-btn"
            title="Wis alle tekeningen"
            onClick={() => drawToolsRef.current && drawToolsRef.current.clearAll()}
          >
            &#9003;
          </button>
        </nav>

        <section className="chart-wrap">
          <div className="chart-overlay-top">
            <span
              className={`over-pill toggle ${vpOn ? "active" : ""}`}
              onClick={toggleVp}
              title="Volume Profile (vPOC/VAH/VAL)"
            >
              Volume Profile
            </span>
            <span
              className={`over-pill toggle ${vwapOn ? "active" : ""}`}
              onClick={toggleVwap}
            >
              VWAP
            </span>
            <span
              className={`over-pill toggle ${atrOn ? "active" : ""}`}
              onClick={toggleAtr}
              title="ATR Grid — kwart-ATR niveaus vanaf de HTF-bodem"
            >
              ATR Grid
            </span>
            <span
              className={`over-pill toggle ${sessionsOn ? "active" : ""}`}
              onClick={toggleSessions}
              title="Asia/London/New York sessie-opens (UTC)"
            >
              Sessies
            </span>
            {ohlc && (
              <span className="ohlc-readout">
                <span style={{ color: ohlc.up ? C.up : C.down }}>
                  O {ohlc.open.toFixed(2)}  H {ohlc.high.toFixed(2)}  L {ohlc.low.toFixed(2)}  C {ohlc.close.toFixed(2)}
                </span>
              </span>
            )}
          </div>
          <div ref={chartContainerRef} className="chart" />
        </section>
      </main>

      <LiveSessionPanel
        cycleStore={liveDrawStoreRef.current}
        shareLabel="Your drawings (trend lines, zones, etc.) are shared live in this room."
      />
    </div>
  );
}
